// The signed-in frame: who you are, which company, and where you can go.
//
// Phase 7 replaced a horizontal tab bar with the sidebar, ported from Zanetto
// Tools — see docs/phase-7-parity.md. Phase 8's DM1 replaced the phone
// hamburger-and-drawer with BOTTOM TABS (Nathan's call, 2026-08-21): on a
// phone the sidebar's destinations become a thumb-reachable bar of four plus
// More, the pattern the ZT mobile plan prescribes. Desktop is unchanged.
//
// The navigation itself — the items, the icons, the role filtering — lives in
// navConfig.tsx, shared with the More page, so the sidebar, the tab bar and
// the More list can never disagree about who sees what.
import { Suspense, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useSession } from '../session';
import { notifications } from '../notificationsApi';
import { notificationsFeed } from '../homeApi';
import { BrandLockup, BrandMark } from './BrandMark';
import { NavGroup } from './NavGroup';
import { ErrorBoundary } from './ErrorBoundary';
import { PageLoading } from './PageLoading';
import { ownerKey } from '../offline/localForm';
import { useSendRunner } from '../offline/runner';
import { useBrowserOnline, useGuardedSignOut, useKeepPackFresh } from '../offline/hooks';
import {
  GUIDES_ICON,
  GUIDES_URL,
  MEGAPHONE_ICON,
  MORE_ICON,
  NOTIFICATIONS_NAV,
  PLATFORM_NAV,
  SETTINGS_NAV,
  mainNav,
  moreDestinations,
  phoneTabTargets,
  ROLE_LABEL,
  type Role,
} from './navConfig';

/**
 * What the shell hands its pages through the Outlet.
 *
 * The Notifications badge is polled ONCE, here, on every navigation — so the
 * More page's rendering of the feed entry takes the number from this context
 * rather than fetching its own, and the sidebar and the More row cannot show
 * different figures. It also inherits the field-worker no-poll gate instead
 * of re-implementing it.
 */
export interface ShellOutletContext {
  feedBadge: { count: number; urgent: boolean };
}

/**
 * The unread badge: unseen posts, plus acknowledgements still owed.
 *
 * Refetched on every navigation rather than polled. A poll would be a request
 * per user per interval for a number that only changes when they act on it or
 * when the office posts — and at this scale, seeing a new post on the next
 * click is soon enough.
 */
function useBadge(): { unseen: number; outstanding: number } {
  const [counts, setCounts] = useState({ unseen: 0, outstanding: 0 });
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    void notifications.badge().then(
      (result) => {
        if (!cancelled) setCounts(result);
      },
      () => undefined
    );
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  return counts;
}

/**
 * The Notifications badge: what is overdue or due TODAY (round 37).
 *
 * Not the whole window, which is ZT's rule and its reasoning: with a seven-day
 * lead time an upcoming birthday would sit in the badge for eight days, and a
 * badge that is never zero is one people stop reading. The server computes the
 * number so the badge and the page can never show different figures.
 *
 * MANAGER+ ONLY, and this is the third of the three places that gate has to
 * hold — the nav entry and the route are the other two. Without it a field
 * worker polls a 403 on every navigation for a badge they will never see.
 */
function useFeedBadge(role: Role): { count: number; urgent: boolean } {
  const [counts, setCounts] = useState({ count: 0, urgent: false });
  const location = useLocation();
  const allowed = role !== 'field';

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    void notificationsFeed.load().then(
      (result) => {
        // Defensive on the shape as well as the request: this sits in the
        // chrome of every page, and a badge is never worth throwing the whole
        // shell away for.
        if (!cancelled) {
          setCounts({
            count: result?.counts?.badge ?? 0,
            urgent: (result?.counts?.overdue ?? 0) > 0,
          });
        }
      },
      () => undefined
    );
    return () => {
      cancelled = true;
    };
  }, [location.pathname, allowed]);

  return counts;
}

/**
 * A nested group entry (Resources -> People, Assets, ...). Indented, smaller,
 * and deliberately quieter than a top-level link: it is a shortcut into a page
 * the parent already reaches, not a sixth destination competing with the five.
 */
const childLinkClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center rounded-md py-1.5 pl-11 pr-3 text-[0.8125rem] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
    isActive ? 'bg-ink-700 font-medium text-white' : 'text-slate-400 hover:text-white'
  }`;

/**
 * `section` is the NavGroup's third state, ported from ZT: a collapsed group
 * whose section holds the current page still shows where you are, in the
 * hover shade rather than the active brand fill.
 */
const linkClass = ({ isActive, section = false }: { isActive: boolean; section?: boolean }) =>
  `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
    isActive
      ? 'bg-brand-500 font-medium text-ink shadow-sm'
      : section
        ? 'bg-ink-800 text-white'
        : 'text-slate-300 hover:bg-ink-800 hover:text-white'
  }`;

/** A tab is ≥52px tall — comfortably past the 44px touch-target floor. */
const tabClass = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-[3.25rem] flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[0.65rem] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-400 ${
    isActive ? 'text-brand-400' : 'text-slate-300'
  }`;

export function Badge({ count, urgent, floating }: { count: number; urgent: boolean; floating?: boolean }) {
  if (count === 0) return null;
  return (
    <span
      className={`inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums ${
        urgent ? 'bg-red-600 text-white' : 'bg-slate-600 text-white'
      } ${floating ? 'absolute -right-3 -top-1' : ''}`}
    >
      {count}
    </span>
  );
}

export function AppShell() {
  const { user, tenant, offline } = useSession();
  // Offline form fill (round 42): forms finished on this phone keep sending
  // for as long as the app is open, the saved forms list stays fresh while
  // there is signal, and signing out says so when something has not gone.
  const owner = user && tenant ? ownerKey(tenant.id, user.id) : null;
  useSendRunner(owner);
  useKeepPackFresh(owner);
  const signOut = useGuardedSignOut();
  // Opened with no signal, OR the phone reports losing it mid-session.
  const browserOnline = useBrowserOnline();
  const noSignal = offline || !browserOnline;
  const badge = useBadge();
  const feedBadge = useFeedBadge((user?.role ?? 'field') as Role);
  const location = useLocation();

  if (!user || !tenant) return null;

  const role = user.role as Role;
  const seesFeed = role !== 'field';

  // Outstanding acknowledgements are the loud number — they are something the
  // person has to do, not merely something they have not looked at.
  const badgeCount = badge.outstanding > 0 ? badge.outstanding : badge.unseen;
  const badgeUrgent = badge.outstanding > 0;

  const features = { timesheets: tenant.timesheetsEnabled };
  const visible = mainNav(role, features).filter(
    (item) => !item.roles || item.roles.includes(role)
  );

  // The phone tabs, in the agreed order rather than sidebar order.
  const tabs = phoneTabTargets(role, features)
    .map((target) => visible.find((item) => item.to === target))
    .filter((item): item is NonNullable<typeof item> => item !== undefined);

  // More lights up when the current page lives behind it, not only on the
  // More screen itself — otherwise Clients would leave the bar with nothing
  // active and the person nowhere.
  const moreActive =
    location.pathname.startsWith('/app/more') ||
    moreDestinations(role, user.isPlatformAdmin, features).some((item) =>
      location.pathname.startsWith(item.to)
    );

  const sidebarBody = (
    <>
      <div className="flex items-center gap-3 border-b border-ink-700 px-5 py-4">
        <div className="min-w-0">
          <BrandLockup ground="onDark" markClass="h-7 w-7" textClass="text-base leading-tight" />
          <p className="truncate text-xs leading-tight text-slate-400" title={tenant.name}>
            {tenant.name}
          </p>
        </div>
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-3" aria-label="Main">
        {visible.map((item) => {
          // Children carry their own role filter where they need one —
          // Templates is manager+ inside a Forms parent everyone sees.
          const kids = item.children?.filter((child) => !child.roles || child.roles.includes(role));
          if (kids && kids.length > 0) {
            return (
              <NavGroup
                key={item.to}
                item={{ ...item, children: kids }}
                linkClass={linkClass}
                childLinkClass={childLinkClass}
              />
            );
          }
          return (
            <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
              {item.icon}
              {item.label}
            </NavLink>
          );
        })}

        {/* Two feeds since round 37, adjacent so the pair reads as a pair.
            Announcements is what somebody wrote and carries the
            acknowledgements badge every role needs; Notifications is what the
            software noticed, and is manager+ in the nav, the route and the
            badge poll alike. */}
        <NavLink to="/app/announcements" className={linkClass}>
          {MEGAPHONE_ICON}
          <span className="flex-1">Announcements</span>
          <Badge count={badgeCount} urgent={badgeUrgent} />
        </NavLink>

        {seesFeed && (
          <NavLink to={NOTIFICATIONS_NAV.to} className={linkClass}>
            {NOTIFICATIONS_NAV.icon}
            <span className="flex-1">{NOTIFICATIONS_NAV.label}</span>
            <Badge count={feedBadge.count} urgent={feedBadge.urgent} />
          </NavLink>
        )}

        {/* Settings is below the divider because it is settings rather than
            work. ONE entry for every role (round 30 folded the four admin
            links and Security into it): the page's own tabs are role-filtered,
            so a field worker's Settings is just Security — the page every role
            needs, because everyone has a password to change. */}
        <div className="my-2 border-t border-ink-700" />
        <NavLink to={SETTINGS_NAV.to} className={linkClass}>
          {SETTINGS_NAV.icon}
          {SETTINGS_NAV.label}
        </NavLink>

        {/* The 37 guides, which the phone has had in More since it existed and
            the desktop had nowhere at all (round 38). It is NOT in `mainNav`:
            every item there is an `/app` route, and nav-config's own test says
            so — this one leaves the app, which is why it is an <a> with a new
            tab rather than a NavLink. A new tab because the reader is
            mid-task: the guide is a second thing open beside the work, not a
            replacement for it. */}
        <a
          href={GUIDES_URL}
          target="_blank"
          rel="noreferrer"
          className={linkClass({ isActive: false })}
        >
          {GUIDES_ICON}
          <span className="flex-1">Help &amp; guides</span>
          <svg
            className="h-3.5 w-3.5 flex-none text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>

        {/* Dayboard's own, and nobody else's — false for every customer
            forever. Hiding it is a courtesy to the sidebar, not the access
            control: the server answers 404 to anyone without the flag. */}
        {user.isPlatformAdmin && (
          <NavLink to={PLATFORM_NAV.to} className={linkClass}>
            {PLATFORM_NAV.icon}
            {PLATFORM_NAV.label}
          </NavLink>
        )}
      </nav>

      <div className="border-t border-ink-700 px-4 py-3">
        <p className="truncate py-0.5 text-xs font-medium text-slate-100" title={user.name}>
          {user.name}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400">{ROLE_LABEL[role]}</span>
          <button
            type="button"
            onClick={() => void signOut()}
            className="-mr-2 rounded px-2 py-1.5 text-xs font-medium text-slate-300 transition-colors hover:bg-ink-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            Sign out
          </button>
        </div>
      </div>
    </>
  );

  // h-screen-safe, not h-screen: 100vh on iOS is measured with Safari's bars
  // collapsed, and this shell scrolls inside <main> so they never collapse —
  // a 100vh shell puts its own bottom edge behind the browser chrome. See the
  // utility's comment in design-system.css.
  return (
    <div className="flex h-screen-safe overflow-hidden bg-paper">
      {/* Static sidebar — desktop only */}
      <aside className="hidden w-56 flex-none flex-col bg-ink text-slate-100 lg:flex">
        {sidebarBody}
      </aside>

      {/* `relative`, and it is load-bearing: nothing else in the shell is
          positioned, so an absolutely-positioned descendant — every Tailwind
          `sr-only` span is one — would otherwise take the DOCUMENT as its
          containing block, escape this overflow, land at its raw in-flow
          offset below the fold, and stretch the window into a second
          scrollbar beside this one. Found on Home (round 36), where the
          getting-started checklist's screen-reader text sat 400px past the
          viewport; invisible to jsdom, which applies no CSS. */}
      <main className="relative flex min-w-0 flex-1 flex-col overflow-y-auto">
        {/* Phone top bar: just the identity. Navigation lives at the bottom. */}
        <div className="sticky top-0 z-30 flex items-center gap-2 bg-ink px-4 py-3 text-slate-100 lg:hidden">
          <BrandMark ground="onDark" className="h-6 w-6 flex-none" />
          <span className="truncate text-sm font-semibold">{tenant.name}</span>
        </div>

        {/* `legacy-page` scopes the transition layer in legacy.css to the routed
            page, so the shell never inherits any of it. It comes off a page as
            that page is rebuilt in idiomatic Tailwind. The extra bottom padding
            below lg is what keeps the last row of content above the tab bar.

            NO max-width, matching ZT's layout — Nathan's call, 2026-08-31,
            widening the same day's schedule-only decision to the whole app.
            The shell caps nothing; a page that wants a reading measure sets
            its own (the non-working-days page's max-w-3xl, the agenda's
            max-w-2xl). Tables and boards get every pixel a wide monitor
            offers, which is ZT's shape and what the schedule was already
            doing. */}
        <div className="legacy-page mx-auto w-full flex-1 p-4 pb-24 lg:p-6 lg:pb-6">
          {/* No signal (round 42): opened on the phone's saved session, or
              the phone reports it has gone. Said once, plainly, so every
              other page's "could not reach" reads as expected rather than
              broken, and the one thing that does work is named. */}
          {noSignal && (
            <div className="banner-info mb-5" role="status">
              <strong>No signal.</strong> You can still fill in forms: they are kept on this phone
              and send when there is signal. Everything else needs a connection.
            </div>
          )}

          {tenant.readOnly && (
            <div className="banner-error mb-5" role="alert">
              This account is read-only. Everything is still here and you can still export it. Add
              a card on the billing page to start making changes again.
            </div>
          )}

          {/* Per-page boundary. It sits around the Outlet rather than around the
              whole shell so a crashing page leaves the navigation working and the
              person can click somewhere else. Keyed on the path so navigating
              away clears the error instead of staying stuck. */}
          <ErrorBoundary key={location.pathname}>
            <Suspense fallback={<PageLoading />}>
              <Outlet context={{ feedBadge } satisfies ShellOutletContext} />
            </Suspense>
          </ErrorBoundary>
        </div>
      </main>

      {/* Phone bottom tabs (DM1). Fixed so the thumb always has them; the
          safe-area padding keeps them clear of the home indicator when the
          site is installed to a home screen (the PWA is the app now). */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 flex border-t border-ink-700 bg-ink pb-[env(safe-area-inset-bottom)] lg:hidden"
        aria-label="Main"
      >
        {tabs.map(({ to, label: fullLabel, tabLabel, icon: glyph, end }) => {
          const label = tabLabel ?? fullLabel;
          return (
            <NavLink key={to} to={to} end={end} className={tabClass}>
              {glyph}
              {label}
            </NavLink>
          );
        })}

        {/* The bar's fixed slot goes to ANNOUNCEMENTS (round 37): the bar is
            full at four plus More, most phone users are field workers, and a
            field worker cannot see the other feed at all. A manager reaches
            Notifications from the sidebar or from More. */}
        <NavLink to="/app/announcements" className={tabClass}>
          <span className="relative">
            {MEGAPHONE_ICON}
            <Badge count={badgeCount} urgent={badgeUrgent} floating />
          </span>
          Posts
        </NavLink>

        <NavLink
          to="/app/more"
          className={() => tabClass({ isActive: moreActive })}
        >
          {MORE_ICON}
          More
        </NavLink>
      </nav>
    </div>
  );
}
